import type { Product } from '#bv/data/products'
import { EliteBadge } from '#bv/components/EliteBadge'
import { FeatureIcon, type FeatureIconName } from '#bv/components/FeatureIcons'

const BADGE_CONFIG: Record<
  NonNullable<Product['badge']>,
  { label: string; className: string }
> = {
  bestseller: { label: 'Bestseller', className: 'badge-bestseller' },
  popular: { label: 'Popular', className: 'badge-popular' },
  new: { label: 'New', className: 'badge-new' },
  limited: { label: 'Limited', className: 'badge-limited' },
  recommended: { label: 'Recommended ⭐', className: 'badge-recommended' },
  elite: { label: 'Elite', className: 'badge-elite' },
}

type Props = {
  product: Product
  imgSrc: string
  selectedVariant: number
  onVariantChange: (index: number) => void
  salePct: number | null
}

export function StandardProductGallery({
  product,
  imgSrc,
  selectedVariant,
  onVariantChange,
  salePct,
}: Props) {
  const badge = product.badge ? BADGE_CONFIG[product.badge] : null

  return (
    <div className="pc-pdp-gallery min-w-0 lg:max-w-[min(100%,26rem)] lg:justify-self-start">
      <div className="flex gap-2.5 sm:gap-3">
        {product.variants && product.variants.length > 0 ? (
          <div className="flex shrink-0 flex-col gap-2 pt-0.5" role="listbox" aria-label="Options">
            {product.variants.map((v, i) => {
              const on = selectedVariant === i
              return (
                <button
                  key={v.value}
                  type="button"
                  role="option"
                  aria-selected={on}
                  onClick={() => onVariantChange(i)}
                  title={v.label}
                  className={`flex h-9 w-9 items-center justify-center rounded-full text-[0.58rem] font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00bfff]/50 sm:h-10 sm:w-10 sm:text-[0.6rem] ${
                    on
                      ? 'scale-[1.02] text-[#f0f6ff] shadow-[0_0_0_2px_#050508,0_0_0_4px_rgba(0, 191, 255,0.85)]'
                      : 'border border-white/12 text-[#5a6d8a] hover:border-[rgba(0, 191, 255,0.4)] hover:text-[#8ecfff]'
                  }`}
                  style={{
                    background: on
                      ? 'linear-gradient(155deg, rgba(0, 191, 255,0.45), rgba(50,8,36,0.95))'
                      : 'rgba(255,255,255,0.035)',
                  }}
                >
                  {v.label.slice(0, 2).toUpperCase()}
                </button>
              )
            })}
          </div>
        ) : null}

        <div className="min-w-0 flex-1">
          <div
            className="relative overflow-hidden rounded-2xl p-px sm:rounded-[1.35rem]"
            style={{
              background:
                'linear-gradient(145deg, rgba(0, 191, 255, 0.18) 0%, rgba(40, 30, 60, 0.45) 42%, rgba(123, 47, 190, 0.14) 100%)',
              boxShadow: '0 18px 56px rgba(0,0,0,0.5), 0 0 48px rgba(0, 191, 255,0.08), 0 0 40px rgba(123, 47, 190,0.06)',
            }}
          >
            <div
              className="relative aspect-square w-full overflow-hidden rounded-[0.95rem] bg-[#030203] sm:rounded-[1.25rem]"
              style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)' }}
            >
              <div
                className="pointer-events-none absolute inset-0 z-0 opacity-90"
                style={{
                  background:
                    'radial-gradient(ellipse 65% 55% at 50% 42%, rgba(0, 191, 255,0.09), transparent 62%)',
                }}
                aria-hidden
              />
              <img
                src={imgSrc}
                alt={product.name}
                className="absolute inset-0 z-[1] h-full w-full object-cover object-center select-none"
                loading="eager"
                decoding="async"
              />
              <div
                className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[22%]"
                style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)' }}
              />
              <div className="pointer-events-none absolute top-2 left-2 z-[2] flex flex-col items-start gap-1.5 sm:top-3 sm:left-3">
                {salePct != null ? (
                  <div
                    className="rounded-md px-2 py-1 text-[0.65rem] font-extrabold uppercase tracking-wider shadow-[0_6px_24px_rgba(0,0,0,0.65)]"
                    style={{
                      background:
                        'linear-gradient(145deg, rgba(12,6,14,0.94) 0%, rgba(45,8,32,0.92) 55%, rgba(120,12,72,0.55) 100%)',
                      border: '1.5px solid rgba(255,77,160,0.85)',
                      color: '#f0f6ff',
                    }}
                  >
                    Sale −{salePct}%
                  </div>
                ) : product.badge === 'elite' ? (
                  <EliteBadge size="sm" rounded="full" />
                ) : badge ? (
                  <div
                    className={`rounded-full px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-wide shadow-lg ${badge.className}`}
                  >
                    {badge.label}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="pc-pdp-trust">
        {(
          [
            { icon: 'zap' as FeatureIconName, title: 'Delivery', text: product.deliveryInfo, color: '#fbbf24' },
            { icon: 'lock' as FeatureIconName, title: 'Secure checkout', text: 'SSL encrypted payment', color: '#60a5fa' },
            { icon: 'shield' as FeatureIconName, title: 'Support', text: '24h help window', color: '#a78bfa' },
          ] as const
        ).map((b) => (
          <div key={b.title} className="pc-pdp-trust__item">
            <span className="pc-pdp-trust__icon" style={{ color: b.color }}>
              <FeatureIcon name={b.icon} size={15} className="text-current" />
            </span>
            <p className="pc-pdp-trust__title">{b.title}</p>
            <p className="pc-pdp-trust__text">{b.text}</p>
          </div>
        ))}
      </div>
    </div>
  )
}
